import React from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { Dialog, DialogTitle, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { path_url } from "../../Config/config";
import { FaShoppingCart } from "react-icons/fa";

const purchaseSchema = Yup.object().shape({
  subject: Yup.string().required("Subject / Order Title is required"),
  amount: Yup.number()
    .typeError("Amount must be a number")
    .required("Amount is required")
    .min(0, "Amount must be 0 or more"),
  duedate: Yup.string().required("Expected delivery/due date is required"),
  priority: Yup.string().required("Priority is required"),
  status: Yup.string().required("Status is required"),
  notes: Yup.string(),
});

const PriorityOptions = [
  "High",
  "Medium",
  "Low",
  "Critical",
  "Normal",
  "Emergency",
  "Deferred",
];

const StatusOptions = [
  "Open",
  "InProgress",
  "Pending",
  "Completed",
  "OnHold",
  "Deferred",
  "Closed",
  "Canceled",
];

export default function CreatePurchaseModal({
  open,
  onClose,
  customerId,
  leadId,
  onPurchaseCreated,
}) {
  const targetId = customerId || leadId;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        style: {
          borderRadius: "12px",
          padding: "8px",
        },
      }}
    >
      <DialogTitle className="d-flex justify-content-between align-items-center pb-2 border-bottom">
        <div
          className="d-flex align-items-center gap-2"
          style={{ color: "#0284c7", fontWeight: 600 }}
        >
          <FaShoppingCart style={{ fontSize: "1.25rem" }} />
          <span>Create Purchase Order</span>
        </div>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className="pt-4">
        <Formik
          initialValues={{
            subject: "",
            amount: "",
            duedate: new Date().toISOString().split("T")[0],
            priority: "Medium",
            status: "Open",
            notes: "",
          }}
          validationSchema={purchaseSchema}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            try {
              const token = localStorage.getItem("accessToken");
              const adminId = localStorage.getItem("adminId");
              // Map to backend ProductOrder entity which populates Details.eventList.orderList
              const orderStatusMap = {
                Open: "Pending",
                InProgress: "Processing",
                Completed: "Delivered",
                OnHold: "OnHold",
                Pending: "Pending",
                Closed: "Delivered",
                Canceled: "Cancelled",
                Deferred: "Pending",
              };
              const payload = {
                customerId: targetId,
                customerid: targetId,
                leadid: targetId,
                subject: values.subject,
                totalprice: values.amount ? String(values.amount) : "0",
                duedate: values.duedate,
                priority: values.priority || "Medium",
                status: orderStatusMap[values.status] || "Pending",
                notes: values.notes,
                useradminid: adminId,
                usercid: adminId,
              };

              let response;
              try {
                response = await axios.post(`${path_url}/productorder`, payload, {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                  },
                });
              } catch (err) {
                // Fallback attempt to task endpoint if productorder returns error
                if (err.response && (err.response.status === 404 || err.response.status === 400)) {
                  response = await axios.post(
                    `${path_url}/task`,
                    { ...payload, status: "Open", type: "Purchase Order" },
                    {
                      headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                      },
                    }
                  );
                } else {
                  throw err;
                }
              }

              if (
                response.data?.statusCodeValue === 200 ||
                response.status === 200 ||
                response.status === 201
              ) {
                toast.success("Purchase Order created successfully!");
                const createdRecord = response.data?.body || payload;
                if (onPurchaseCreated) {
                  onPurchaseCreated(createdRecord);
                }
                resetForm();
                onClose();
              } else {
                toast.error(
                  response.data?.body || "Failed to create purchase order"
                );
              }
            } catch (error) {
              console.error("Purchase creation error:", error);
              toast.error(
                error.response?.data?.message ||
                  error.message ||
                  "Failed to create purchase order"
              );
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
          }) => (
            <Form onSubmit={handleSubmit} className="row g-3 mt-1">
              <div className="col-md-8 mb-3">
                <label className="form-label font-weight-bold">
                  Purchase Order Title / Subject <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="subject"
                  className={`form-control ${
                    touched.subject && errors.subject ? "is-invalid" : ""
                  }`}
                  placeholder="e.g. Raw Material Procurement / Equipment Order"
                  value={values.subject}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.subject && errors.subject && (
                  <div className="invalid-feedback">{errors.subject}</div>
                )}
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label font-weight-bold">
                  Total Amount (₹) <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  name="amount"
                  className={`form-control ${
                    touched.amount && errors.amount ? "is-invalid" : ""
                  }`}
                  placeholder="0.00"
                  value={values.amount}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.amount && errors.amount && (
                  <div className="invalid-feedback">{errors.amount}</div>
                )}
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label font-weight-bold">
                  Expected Due Date <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  name="duedate"
                  className={`form-control ${
                    touched.duedate && errors.duedate ? "is-invalid" : ""
                  }`}
                  value={values.duedate}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.duedate && errors.duedate && (
                  <div className="invalid-feedback">{errors.duedate}</div>
                )}
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label font-weight-bold">
                  Priority <span className="text-danger">*</span>
                </label>
                <select
                  name="priority"
                  className={`form-select ${
                    touched.priority && errors.priority ? "is-invalid" : ""
                  }`}
                  value={values.priority}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {PriorityOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                {touched.priority && errors.priority && (
                  <div className="invalid-feedback">{errors.priority}</div>
                )}
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label font-weight-bold">
                  Status <span className="text-danger">*</span>
                </label>
                <select
                  name="status"
                  className={`form-select ${
                    touched.status && errors.status ? "is-invalid" : ""
                  }`}
                  value={values.status}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {StatusOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                {touched.status && errors.status && (
                  <div className="invalid-feedback">{errors.status}</div>
                )}
              </div>

              <div className="col-md-12 mb-3">
                <label className="form-label font-weight-bold">Order Details & Notes</label>
                <textarea
                  name="notes"
                  rows={3}
                  className="form-control"
                  placeholder="Items list, vendor specifications, payment terms..."
                  value={values.notes}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>

              <div className="col-12 d-flex justify-content-end gap-2 pt-3 border-top">
                <button
                  type="button"
                  className="btn btn-secondary px-4"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary px-4"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Creating..." : "Create Purchase Order"}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
